import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import { SmallText } from 'app/common/typography'
import { colors } from 'shared/assets/styles'
import { ButtonMedium, style } from 'app/assets/styles'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import {
    errors,
    featureCodes,
    orderPickUpType,
    restaurantServices
} from 'shared/utils'
import type { SendOrderPrepare } from 'shared/store/ecommerce/order'
import Modal from 'react-modal'
import type { CacOrder, RestaurantShortDTO } from 'shared/types'
import { AlertModal } from 'app/common/modals/alert'
import { TableServiceModal } from './modal'

const { red, crunchyGreen, crunchyGreenDisabled } = colors
const { PARKING, TABLESERVICE, ONSITE, TAKEAWAY } = orderPickUpType

type Props = {
    prepareOrder: (orderPrepare: SendOrderPrepare) => void,
    handleError: (token: string, missingProducts: boolean) => any,
    navigate: (to: string, state?: any) => void,
    restaurant: RestaurantShortDTO,
    cacOrders: { [string]: CacOrder },
    onSuccess?: () => void,
    isTableServiceAvailable: string => void,
    token: string,
    collectMode: string,
    parkingOpen: boolean,
    alreadyClicked?: boolean,
    fromList: boolean,
    noBottomMargin?: boolean,
    availableFeatures: Array<string>,
    noDisplay?: boolean,
    intl: IntlShape
}

type State = {
    showTableServiceModal: boolean,
    showConfirmationModal: boolean,
    error?: {
        title?: string,
        message: string
    }
}

class _PrepareOrderButton extends Component<Props, State> {
    constructor() {
        super()
        this.state = {
            showTableServiceModal: false,
            showConfirmationModal: false,
            error: undefined
        }
    }

    handleClick = async (pickUpInfo?: string, pickUpType?: string) => {
        const { prepareOrder, onSuccess, token, handleError } = this.props
        let error = {}
        try {
            await prepareOrder({
                token,
                pickUpInfo,
                pickUpType
            })
            onSuccess && onSuccess()
        } catch (e) {
            switch (e) {
                case errors.MISSING_PRODUCTS:
                    await handleError(token, true)
                    this.navigateToCartWithError(
                        'clickandcollect.error.missing'
                    )
                    break
                case errors.NETWORK:
                    error.message = 'error.network.alert.message'
                    break
                case errors.REJECTED:
                    await handleError(token, false)
                    this.navigateToCartWithError()
                    break
                case errors.RESTAURANT_CLOSED:
                    this.navigateToCartWithError(
                        'clickandcollect.error.restaurant.closed'
                    )
                    break
                case errors.CAC_PREPARATION_CLOSED:
                    error.title = 'clickandcollect.collect.cac.closed.title'
                    error.message = 'clickandcollect.collect.cac.closed'
                    break
                case errors.FEATURE_NOT_AVAILABLE:
                    error.title = 'clickandcollect.error.closed.title'
                    error.message = 'clickandcollect.error.closed'
                    break
                default:
                    error.message = 'form.error.default'
                    break
            }
        } finally {
            this.setState({
                error
            })
        }
    }

    navigateToCartWithError = (reason?: string) => {
        const { navigate, intl } = this.props
        const to = intl.formatMessage({
            id: 'routes.clickandcollect.cart.summary'
        })
        const state = {
            error: {
                message: 'clickandcollect.error.payment.validation',
                reason
            }
        }
        navigate(to, state)
    }

    toggleTableServiceModal = () => {
        const { showTableServiceModal } = this.state
        this.setState({ showTableServiceModal: !showTableServiceModal })
    }

    toggleConfirmationModal = () => {
        const { showConfirmationModal } = this.state
        this.setState({ showConfirmationModal: !showConfirmationModal })
    }

    openPreparationModal = async () => {
        const { collectMode, isTableServiceAvailable, restaurant } = this.props
        const isClickAndStay =
            collectMode === TABLESERVICE || collectMode === ONSITE
        const isTableServiceActive =
            isClickAndStay && (await isTableServiceAvailable(restaurant.id))
        isTableServiceActive
            ? this.toggleTableServiceModal()
            : this.toggleConfirmationModal()
    }

    render() {
        const {
            collectMode,
            alreadyClicked,
            parkingOpen,
            fromList,
            intl,
            restaurant,
            noBottomMargin,
            availableFeatures,
            noDisplay
        } = this.props
        const {
            error,
            showTableServiceModal,
            showConfirmationModal
        } = this.state

        const { services } = restaurant || {}
        const hasTerrace =
            services && services.includes(restaurantServices.TERRACE)
        const buttonDisabled = !availableFeatures.includes(
            featureCodes.CLICK_AND_COLLECT
        )

        return (
            <Fragment>
                <Modal
                    isOpen={showTableServiceModal}
                    shouldCloseOnOverlayClick
                    onRequestClose={this.toggleTableServiceModal}
                    style={style.classicModalStyle}>
                    <TableServiceModal
                        hasTerrace={hasTerrace}
                        handleClick={this.handleClick}
                        handleClose={this.toggleTableServiceModal}
                    />
                </Modal>
                <AlertModal
                    isOpen={showConfirmationModal}
                    alertTitleKey={
                        collectMode !== PARKING || parkingOpen
                            ? 'clickandcollect.cart.confirmation'
                            : 'clickandcollect.parking.closed'
                    }
                    alertContentKey={
                        collectMode !== PARKING || parkingOpen
                            ? 'clickandcollect.confirmation.message'
                            : 'clickandcollect.parking.closed.message'
                    }
                    okLabel="yes.sure"
                    leftLabel="no.later"
                    handleClose={this.toggleConfirmationModal}
                    onOkClick={() => {
                        this.handleClick(
                            undefined,
                            collectMode !== PARKING || parkingOpen
                                ? undefined
                                : TAKEAWAY
                        )
                        this.toggleConfirmationModal()
                    }}
                    onLeftClick={this.toggleConfirmationModal}
                />
                <StyledButtonMedium
                    noDisplay={noDisplay}
                    fromList={fromList}
                    background={
                        alreadyClicked ? crunchyGreenDisabled : crunchyGreen
                    }
                    disabled={alreadyClicked || buttonDisabled}
                    noBottomMargin={noBottomMargin}
                    onClick={this.openPreparationModal}>
                    <FormattedMessage id="clickandcollect.order.start">
                        {(text: string) => <span>{text}</span>}
                    </FormattedMessage>
                </StyledButtonMedium>
                {alreadyClicked && (
                    <FormattedMessage id="clickandcollect.confirmation.alreadyClicked">
                        {(text: string) => (
                            <SmallText color={red}>{text}</SmallText>
                        )}
                    </FormattedMessage>
                )}
                {!!error && !!error.message && (
                    <AlertModal
                        alertTitle={intl.formatMessage({
                            id: error.title || 'oops'
                        })}
                        alertContentKey={error.message}
                        isOpen
                        handleClose={() => this.setState({ error: undefined })}
                    />
                )}
            </Fragment>
        )
    }
}

const StyledButtonMedium = styled(ButtonMedium)`
    border: none;
    border-radius: 10px;
    align-self: center;
    margin: ${props => (props.fromList ? `14px 0 10px` : `14px 0 22px`)};
    ${props => props.noBottomMargin && `margin-bottom: 0`};
    width: 100%;
    min-width: 240px;
    ${props => props.noDisplay && `display: none`};
`

export const PrepareOrderButtonComponent = injectIntl(_PrepareOrderButton)
